import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';

const FlexibleChart = ({ data }) => {
  // State for chart configuration
  const [chartType, setChartType] = useState('line');
  const [xAxis, setXAxis] = useState(null);
  const [yAxis, setYAxis] = useState(null);

  // Reference for SVG and container
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  // Get column names from data
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  // Effect to render chart when configuration changes
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !xAxis || !yAxis) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Get container dimensions
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    // Set up dimensions
    const margin = { top: 20, right: 20, bottom: 50, left: 50 };
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr('width', containerWidth)
      .attr('height', containerHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    const xScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => +d[xAxis])])
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => +d[yAxis])])
      .range([height, 0]);

    // Add X axis
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .append('text')
      .attr('x', width / 2)
      .attr('y', 35)
      .attr('fill', 'black')
      .attr('text-anchor', 'middle')
      .text(xAxis);

    // Add Y axis
    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -height / 2)
      .attr('fill', 'black')
      .attr('text-anchor', 'middle')
      .text(yAxis);

    // Render chart based on type
    if (chartType === 'line') {
      // Line chart
      const line = d3.line()
        .x(d => xScale(+d[xAxis]))
        .y(d => yScale(+d[yAxis]));

      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', 'steelblue')
        .attr('stroke-width', 2)
        .attr('d', line);

      // Add data points
      svg.selectAll('circle')
        .data(data)
        .enter()
        .append('circle')
        .attr('cx', d => xScale(+d[xAxis]))
        .attr('cy', d => yScale(+d[yAxis]))
        .attr('r', 5)
        .attr('fill', 'steelblue');
    } else {
      // Bar chart
      svg.selectAll('rect')
        .data(data)
        .enter()
        .append('rect')
        .attr('x', d => xScale(+d[xAxis]))
        .attr('y', d => yScale(+d[yAxis]))
        .attr('width', width / data.length - 10)
        .attr('height', d => height - yScale(+d[yAxis]))
        .attr('fill', 'steelblue');
    }
  }, [data, chartType, xAxis, yAxis]);

  // Render configuration controls
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-2 bg-gray-100 border-b">
        <div className="flex space-x-2 justify-center items-center">
          {/* Chart Type Selection */}
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            className="flex-1 border p-1 rounded text-sm"
          >
            <option value="line">Line Chart</option>
            <option value="bar">Bar Chart</option>
          </select>

          {/* X-Axis Column Selection */}
          <select
            value={xAxis || ''}
            onChange={(e) => setXAxis(e.target.value)}
            className="flex-1 border p-1 rounded text-sm"
          >
            <option value="">X-Axis</option>
            {columns.map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>

          {/* Y-Axis Column Selection */}
          <select
            value={yAxis || ''}
            onChange={(e) => setYAxis(e.target.value)}
            className="flex-1 border p-1 rounded text-sm"
          >
            <option value="">Y-Axis</option>
            {columns.map(col => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Chart Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden relative"
      >
        {!xAxis || !yAxis ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            <p>Select X and Y axes to visualize data</p>
          </div>
        ) : (
          <svg ref={svgRef} className="w-full h-full"></svg>
        )}
      </div>
    </div>
  );
};

export default FlexibleChart;